<script setup lang="ts">
import { storeToRefs } from 'pinia';
import type { MenuItem } from 'primevue/menuitem';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import { useProductGroupStore } from 'shared/products/groups/stores/productGroupStore';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { clearCorruptDataTableState } from 'shared/common/dataTableState';
import { useDataTableMultiSort } from 'shared/common/composables/useDataTableMultiSort';
import { useColumnSelector, type ColumnField } from 'shared/common/composables/useColumnSelector';
import { useColumnFilters } from 'shared/common/composables/useColumnFilters';
import ColumnSelectorButton from 'shared/common/components/ColumnSelectorButton.vue';
import ColumnFilterCell from 'shared/common/components/ColumnFilterCell.vue';
import { useProductGroupAuditApi } from '../api/productGroupAuditApi';
import type { DataTableRowClickEvent, DataTableRowDoubleClickEvent } from 'primevue/datatable';
import { Button, Column, DataTable, Card, InputText, Menu } from 'primevue';
import EditProductGroupDialog from './EditProductGroupDialog.vue';
import DeleteProductGroupDialog from './DeleteProductGroupDialog.vue';
import AuditDialog from 'shared/audit/components/AuditDialog.vue';
import { useRefereshProductGroupStore } from '../commands/refreshProductGroupStore';
import { ProductType } from 'shared/products/domain/productType.ts';
import { useFeatureFlagStore } from '@/featureFlags/stores/featureFlagStore.ts';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const store = useProductGroupStore();
const refereshProductGroupStore = useRefereshProductGroupStore();
const featureFlags = useFeatureFlagStore();

const productGroupAuditApi = useProductGroupAuditApi();

const types = ref<ProductType[]>([]);

if(featureFlags.isLogYardEnabled())types.value.push(ProductType.Log);
if(featureFlags.isBulkEnabled())types.value.push(ProductType.Bulk);
if(featureFlags.isCoilEnabled())types.value.push(ProductType.Coil);

const groups = computed<ProductGroupItem[]>(() => {
  const result = store.groups.map((g) => {
    return {
      id: g.id,
      name: g.name,
      type: translations.value.productType(g.productType),
      color: g.color,
      nameSortOrder: 0,
    };
  });

  result.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

  for (let i = 0; i < result.length; i++) {
    const item = result[i];
    if (item) {
      item.nameSortOrder = i;
    }
  }

  return result;
});

const selectedGroups = ref<ProductGroupItem[]>([]);
const loading = ref(false);
const menuGroupId = ref<string>();

const groupMenu = useTemplateRef('group-menu');
const editDialog = useTemplateRef('edit-dialog');
const deleteDialog = useTemplateRef('delete-dialog');
const auditDialog = useTemplateRef('audit-dialog');
const actionFailedAlert = useActionFailedAlert();
const { multiSortMeta, sortBadgeClass } = useDataTableMultiSort();

interface GroupColumn extends ColumnField {
  sortable: boolean;
  sortField?: string;
  cellClass?: string;
  kind: 'text' | 'color';
}

const columnDefinitions = computed<GroupColumn[]>(() => [
  { field: 'name', header: translations.value.generic.name, sortable: true, sortField: 'nameSortOrder', cellClass: 'product-group-name', kind: 'text', filterType: 'text' },
  { field: 'type', header: translations.value.generic.type, sortable: true, kind: 'text', filterType: 'text' },
  { field: 'code', header: translations.value.generic.code, sortable: true, kind: 'text', filterType: 'text' },
  { field: 'color', header: translations.value.generic.color, sortable: false, kind: 'color' },
]);
const { orderedColumns, selectedColumns, setSelectedColumns, setOrder, reset: resetColumns } =
  useColumnSelector<GroupColumn>({ storageKey: 'product-groups-table-columns', columns: columnDefinitions });

const { filters, filteredCount, hasActiveFilters, clearFilters, onFilter } = useColumnFilters({
  columns: columnDefinitions,
  totalCount: () => groups.value.length,
});

// Clear corrupt persisted sort state before the DataTable restores it while rendering.
clearCorruptDataTableState('product-groups-table-state');

onMounted(async () => {
  refereshProductGroupStore.initialize();
});

const menuItems = computed<MenuItem[]>(() => [
  {
    label: translations.value.generic.edit,
    icon: 'pi pi-pencil',
    command() {
      const id = menuGroupId.value;
      if (!id) return;
      show(id);
    },
  },
  {
    label: translations.value.generic.copy,
    icon: 'pi pi-copy',
    command() {
      const id = menuGroupId.value;
      if (!id) return;
      editDialog.value?.show({ copyGroupId: id });
    },
  },
  {
    label: translations.value.generic.delete,
    icon: 'pi pi-trash',
    command() {
      const id = menuGroupId.value;
      if (!id) return;
      deleteDialog.value?.show(id);
    },
  },
  {
    label: translations.value.generic.audit,
    icon: 'pi pi-search',
    async command() {
      const id = menuGroupId.value;
      if (!id) return;

      const response = await productGroupAuditApi.execute({
        id: id,
      });

      if (!response.ok) {
        actionFailedAlert.show(response.details);
        return;
      }

      auditDialog.value?.show({
        audit: response.audit,
      });
    },
  },
]);

function onAddNewClick() {
  editDialog.value?.show({});
}

function onDoubleClick(ev: DataTableRowDoubleClickEvent) {
  const group = ev.data as ProductGroupItem;
  show(group.id);
}

function show(id: string) {
  editDialog.value?.show({ groupId: id });
}

function onClick(ev: DataTableRowClickEvent) {
  const target = ev.originalEvent.target as HTMLElement | null;
  if (!target) return;
  const name = target.closest('.product-group-name');
  if (!name) return;
  show(ev.data.id);
}

function toggleMenu(ev: Event, groupId: string) {
  menuGroupId.value = groupId;
  groupMenu.value?.toggle(ev);
}

interface ProductGroupItem {
  id: string;
  name: string;
  type: string;
  color: string | null;
  nameSortOrder: number;
}
</script>

<template>
  <Card class="product-group-card">
    <template #title>
      <span>{{ translations.productGroups.listPage.title }}</span>
      <div class="filter-info">
        <span v-if="hasActiveFilters && filteredCount < groups.length" class="filtered-count">
          ({{ translations.dataTable.showing }} {{ filteredCount }} {{ translations.dataTable.of }} {{ groups.length }})
        </span>
        <Button v-if="hasActiveFilters" type="button" severity="secondary" size="small" icon="pi pi-filter-slash"
          :label="translations.dataTable.clearFilters" @click="clearFilters" />
      </div>
      <Button v-if="types.length > 0" class="add-button" :label="translations.generic.add" @click="onAddNewClick">
      </Button>
      <InputText class="search-field" autofocus v-model="filters.global.value"
        :placeholder="translations.generic.search" />
    </template>
    <template #content>
      <DataTable class="product-group-table" sort-mode="multiple" v-model:multiSortMeta="multiSortMeta" :class="sortBadgeClass" v-model:selection="selectedGroups"
        v-model:filters="filters" filterDisplay="menu" @filter="onFilter" :value="groups" data-key="id" scrollable scrollHeight="flex" meta-key-selection
        @row-dblclick="onDoubleClick" :loading="loading" @row-click="onClick" stateStorage="local" stateKey="product-groups-table-state">
        <template #empty>{{ translations.productGroups.listPage.empty }}</template>
        <template #loading>{{ translations.productGroups.listPage.loading }}</template>
        <Column v-for="col of selectedColumns" :key="col.field" :field="col.field" :class="col.cellClass"
          :header="col.header" :sortable="col.sortable" :sort-field="col.sortField"
          :filter="!!col.filterType" :filterField="col.filterField ?? col.field"
          :showFilterMatchModes="col.showFilterMatchModes" :showFilterOperator="col.showFilterOperator"
          :dataType="col.filterType === 'numeric' ? 'numeric' : undefined">
          <template #body="{ data }">
            <template v-if="col.kind === 'color'">
              <div v-if="data.color" class="flex gap-2">
                <div class="w-6 h-6 rounded" :style="{ backgroundColor: `${data.color}` }">
                </div>
              </div>
            </template>
            <template v-else>
              {{ data[col.field] }}
            </template>
          </template>
          <template v-if="col.filterType" #filter="{ filterModel, filterCallback }">
            <ColumnFilterCell :filter-type="col.filterType" :filter-model="filterModel" :filter-callback="filterCallback"
              :options="col.filterOptions"
              :placeholder="translations.dataTable.filterBy + ' ' + col.header.toLocaleLowerCase()" />
          </template>
        </Column>
        <Column class="w-24 !text-end">
          <template #header>
            <ColumnSelectorButton :columns="orderedColumns" :modelValue="selectedColumns"
              @update:modelValue="setSelectedColumns" @reorder="setOrder" @reset="resetColumns" />
          </template>
          <template #body="group">
            <Button icon="pi pi-ellipsis-v" variant="text" severity="secondary" rounded
              @click="(ev) => toggleMenu(ev, group.data.id)"></Button>
          </template>
        </Column>
      </DataTable>
    </template>
  </Card>

  <EditProductGroupDialog ref="edit-dialog"></EditProductGroupDialog>
  <DeleteProductGroupDialog ref="delete-dialog"></DeleteProductGroupDialog>
  <AuditDialog ref="audit-dialog"></AuditDialog>
  <Menu ref="group-menu" :model="menuItems" :popup="true"></Menu>
</template>

<style scoped>
:deep(.p-datatable-hoverable .p-datatable-selectable-row) {
  cursor: unset;
}

:deep(.p-card-title) {
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  column-gap: 0.5rem;
  align-items: center;
}

.filter-info {
  grid-column: 2;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}

.filtered-count {
  font-weight: 400;
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}

.add-button {
  grid-column: 3;
}

.search-field {
  grid-column: 4;
}

:deep(.product-group-name) {
  cursor: pointer;
}

:deep(.product-group-name:hover) {
  color: var(--p-primary-900);
  background-color: var;
  text-decoration: underline;
}

.product-group-card {
  overflow-y: hidden;
  height: 100%;
}

:deep(.p-card-body) {
  overflow-y: hidden;
  height: 100%;
}

:deep(.p-card-content) {
  overflow-y: hidden;
  height: 100%;
}
</style>
